import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Facebook, Instagram, Plus, ArrowRight, Loader2, RefreshCw, AlertTriangle, Trash2,
  Play, Pause, History, Zap, Link2, PlugZap, ShieldOff,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const META_ERRORS = {
  expired: "That connection link expired. Please start again.",
  nopages: "No Facebook Pages found. You need to be an admin of at least one Page.",
};

const STATUS = {
  connected: { label: "Connected", cls: "bg-green-50 text-green-600 border-green-200", dot: "bg-green-500" },
  error: { label: "Connection lost", cls: "bg-red-50 text-red-600 border-red-200", dot: "bg-red-500" },
  disconnected: { label: "Disconnected", cls: "bg-slate-100 text-slate-500 border-slate-200", dot: "bg-slate-400" },
};

export default function Accounts() {
  const { ClientPicker, scopeParam, ready } = useClientScope();
  const { user } = useAuth();
  const canWrite = user?.role !== "client_viewer";
  const [accounts, setAccounts] = useState(null);
  const [busy, setBusy] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [pending, setPending] = useState(null);   // { token, pages } after a real Meta connect
  const [picks, setPicks] = useState({});
  const [linking, setLinking] = useState(false);

  const load = useCallback(() => {
    if (!ready) return;
    const q = scopeParam ? `?${scopeParam}` : "";
    api.get(`/accounts${q}`).then(({ data }) => setAccounts(data)).catch(() => setAccounts([]));
  }, [scopeParam, ready]);

  useEffect(() => { load(); }, [load]);

  // Meta redirects the browser back here after the OAuth handshake. A success carries a
  // one-time token naming the Pages this account administers, which the user then picks from.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const selectToken = params.get("meta_select");
    const error = params.get("meta_error");
    if (!selectToken && !error) return;
    window.history.replaceState({}, "", window.location.pathname);

    if (error) {
      toast.error(META_ERRORS[error] || "Couldn't complete the Facebook connection. Please try again.");
      return;
    }
    api.get(`/meta/oauth/pending/${selectToken}`)
      .then(({ data }) => setPending({ token: selectToken, pages: data.pages }))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  useEffect(() => {
    const t = setInterval(load, 8000); // reflect near-real-time catch-up + scan progress
    return () => clearInterval(t);
  }, [load]);

  const connect = async (platform) => {
    // A super admin connects on behalf of the client picked above; with no clients yet there's nothing to attach to
    if (!ready) {
      toast.error("Create a client on the Clients page first, then pick it here before connecting.");
      return;
    }
    setBusy(`connect-${platform}`);
    try {
      const q = scopeParam ? `?platform=${platform}&${scopeParam}` : `?platform=${platform}`;
      const { data } = await api.get(`/meta/oauth/start${q}`);
      if (data.auth_url) {
        window.location.href = data.auth_url;   // busy stays set while the browser leaves
        return;
      }
      toast.info(data.message || "Facebook login isn't configured on this server.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setBusy("");
  };

  const togglePick = (pageId, platform) =>
    setPicks((prev) => {
      const key = `${pageId}::${platform}`;
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });

  const finalizeLink = async () => {
    const selections = Object.keys(picks).map((key) => {
      const [page_id, platform] = key.split("::");
      return { page_id, platform };
    });
    if (!selections.length) return;
    setLinking(true);
    try {
      const { data } = await api.post("/meta/oauth/finalize", { token: pending.token, selections });
      const n = data.connected.length;
      // Live accounts enforce immediately — see docs/adr/0008-immediate-enforcement-in-live-mode.md
      toast.success(`${n} account${n === 1 ? "" : "s"} connected. Moderation is now live on ${n === 1 ? "it" : "them"}.`);
      setPending(null);
      setPicks({});
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLinking(false); }
  };

  const act = async (id, path, label) => {
    setBusy(`${path}-${id}`);
    try {
      await api.post(`/accounts/${id}/${path}`, path === "simulate" ? { count: 5 } : {});
      if (label) toast.success(label);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const remove = async () => {
    try {
      await api.delete(`/accounts/${toDelete.id}`);
      toast.success("Account removed");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">Connect & monitor</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Connected Accounts</h1>
          </div>
          <ClientPicker />
        </div>

        {/* Connect cards */}
        {canWrite && <div>
          <ConnectCard
            icon={Facebook} title="Facebook Page"
            desc="Monitor comments on your Page posts."
            onConnect={() => connect("facebook")}
            loading={busy === "connect-facebook"} testid="connect-facebook"
          />
        </div>}

        {/* Accounts list */}
        {accounts === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /></div>
        ) : accounts.length === 0 ? (
          <div className="bg-card rounded-2xl border border-slate-100 p-12 text-center">
            <Link2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{canWrite ? "No accounts connected yet. Use a Connect button above." : "No accounts connected yet."}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {accounts.map((a) => {
              const st = STATUS[a.status] || STATUS.disconnected;
              const scan = a.history_scan;
              const scanning = scan?.status === "running";
              return (
                <div key={a.id} data-testid="account-card" className="bg-card rounded-2xl border border-slate-100 shadow-card p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center text-slate-700 bg-slate-100 border border-slate-200 shrink-0`}>
                        {a.platform === "facebook" ? <Facebook className="w-5 h-5" /> : <Instagram className="w-5 h-5" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-slate-900 truncate">{a.name}</p>
                          <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{a.mode}</span>
                          {a.paused && <span className="text-[10px] font-semibold uppercase text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Paused</span>}
                        </div>
                        <p className="text-xs text-slate-400 truncate">{a.handle} · {a.pending_review} to review · {a.removed_count} removed</p>
                      </div>
                    </div>

                    <div className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border ${st.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}
                    </div>

                    {canWrite && <div className="flex items-center gap-1.5 flex-wrap">
                      {a.status === "error" ? (
                        <IconBtn onClick={() => act(a.id, "reconnect", "Reconnected")} testid={`reconnect-${a.id}`} title="Reconnect" tone="indigo"><PlugZap className="w-4 h-4" /></IconBtn>
                      ) : (
                        <IconBtn onClick={() => act(a.id, "simulate-break", "Marked as broken (demo)")} testid={`break-${a.id}`} title="Simulate connection break"><ShieldOff className="w-4 h-4" /></IconBtn>
                      )}
                      <IconBtn onClick={() => act(a.id, "pause", a.paused ? "Resumed" : "Paused")} testid={`pause-${a.id}`} title={a.paused ? "Resume" : "Pause"}>
                        {a.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                      </IconBtn>
                      <button
                        onClick={() => act(a.id, "simulate", "New comments pulled in")}
                        disabled={busy === `simulate-${a.id}` || a.status !== "connected"}
                        data-testid={`simulate-${a.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 text-xs font-semibold disabled:opacity-50"
                      >
                        {busy === `simulate-${a.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Pull comments
                      </button>
                      <IconBtn onClick={() => act(a.id, "history-scan", "History scan started")} disabled={scanning} testid={`scan-${a.id}`} title="Scan older comments"><History className="w-4 h-4" /></IconBtn>
                      <IconBtn onClick={() => setToDelete(a)} testid={`delete-account-${a.id}`} title="Remove" tone="red"><Trash2 className="w-4 h-4" /></IconBtn>
                    </div>}
                  </div>

                  {a.status === "error" && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                      <AlertTriangle className="w-4 h-4" /> Connection broke — comments aren't being monitored. Click reconnect.
                    </div>
                  )}
                  {scan && scan.total > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                        <span>{scanning ? "Scanning older comments…" : "History scan complete"}</span>
                        <span>{scan.done}/{scan.total}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 transition-all" style={{ width: `${(scan.done / scan.total) * 100}%` }} />
                      </div>
                    </div>
                  )}
                  {scan?.status === "failed" && (
                    <p className="mt-3 text-xs text-red-600">History scan failed — couldn't read comments from {a.platform === "instagram" ? "Instagram" : "Facebook"}.</p>
                  )}
                  {scan?.status === "complete" && scan.total === 0 && (
                    <p className="mt-3 text-xs text-slate-500">History scan complete — no comments found.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) { setPending(null); setPicks({}); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="meta-page-picker">
          <DialogHeader>
            <DialogTitle>Choose what to monitor</DialogTitle>
            <DialogDescription>
              These are the Pages you administer. Connected accounts are moderated live —
              comments matching your rules will be hidden on the real page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {(pending?.pages || []).map((pg) => (
              <div key={pg.page_id} className="rounded-xl border border-slate-100 p-3">
                <p className="font-semibold text-slate-900 text-sm">{pg.page_name}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <PickChip
                    checked={!!picks[`${pg.page_id}::facebook`]}
                    onClick={() => togglePick(pg.page_id, "facebook")}
                    icon={Facebook}
                    label="Facebook Page"
                    testid={`pick-fb-${pg.page_id}`}
                  />
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <button
              onClick={() => { setPending(null); setPicks({}); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              onClick={finalizeLink}
              disabled={linking || !Object.keys(picks).length}
              data-testid="confirm-meta-link"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 text-sm font-semibold disabled:opacity-50"
            >
              {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Connect {Object.keys(picks).length || ""}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This disconnects the account and deletes its monitored comments.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-account" className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function ConnectCard({ icon: Icon, title, desc, onConnect, loading, testid }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 via-card to-card shadow-card p-5 flex items-center gap-4">
      <div aria-hidden="true" className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="relative w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-indigo-600 text-indigo-50">
        <Icon className="w-6 h-6" />
      </div>
      <div className="relative flex-1 min-w-0">
        <p className="font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
      <button onClick={onConnect} disabled={loading} data-testid={testid} className="relative inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 text-sm font-semibold disabled:opacity-60">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Connect
        {!loading && <ArrowRight className="w-4 h-4" />}
      </button>
    </div>
  );
}

function PickChip({ checked, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition ${
        checked
          ? "bg-indigo-600 border-indigo-600 text-indigo-50"
          : "bg-card border-slate-200 text-slate-600 hover:border-indigo-300"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function IconBtn({ children, onClick, title, testid, tone = "slate", disabled }) {
  const tones = {
    slate: "text-slate-500 hover:text-slate-800 hover:bg-slate-100",
    indigo: "text-indigo-600 hover:bg-indigo-50",
    red: "text-slate-400 hover:text-red-600 hover:bg-red-50",
  };
  return (
    <button onClick={onClick} title={title} data-testid={testid} disabled={disabled}
      className={`p-2 rounded-lg transition disabled:opacity-40 ${tones[tone]}`}>
      {children}
    </button>
  );
}
